import React from "react";
import { useState, useEffect, useMemo } from "react";
import { motion } from "framer-motion";

import { useFormik, Form, FormikProvider } from "formik";
import * as Yup from "yup";

import CommercialPlanPanel from "../../components/CommercialPlanPanel";
import { useAppStore } from "../../shared/stores/useAppStore";
import { UTCOffset } from "../../components/HistoryModal";
import motionify from "../../components/Motion";
import ErpConnectionsModal from "../../components/ErpConnections";

import TailwindButton from "../../components/Tailwind/TailwindButton";
import TailwindInput from "../../components/Tailwind/TailwindInput";
import TailwindModal from "../../components/Tailwind/TailwindModal";
import TailwindSelect from "../../components/Tailwind/TailwindSelect";
import { cn } from "../../lib/utils";
import { useOrganization, useOrganizationUsers } from "../../shared/hooks/queries/useOrganization";
import { useEditOrganization, useAddUser, useEditRoleUser, useDeleteUser } from "../../shared/hooks/mutations/useOrganizationMutations";
import { ApiError } from "../../shared/api/organization";

//////////////////////////////////////////////////////////////////////////////////////////////////////////
//                                    Helpers                                                           //
//////////////////////////////////////////////////////////////////////////////////////////////////////////

const RoleEnum = {
  admin: 'Administrator',
  user: 'User',
  billing: 'Billing',
};

const RoleBadgeColors = {
  admin: 'bg-primary/10 text-primary',
  user: 'bg-slate-100 text-slate-600',
  billing: 'bg-amber-50 text-amber-700',
};

const getCurrentTzDate = (dateStr: string) => {
  const utcDate = new Date(dateStr);
  return new Date(utcDate.getTime() + UTCOffset * 60000);
};

const formatDate = (date: Date) => {
  if (useAppStore.getState().userSettings.dateSystem == 'DMY') {
    return date.toLocaleString('en-GB');
  } else {
    return date.toLocaleString('en-US');
  }
};

//////////////////////////////////////////////////////////////////////////////////////////////////////////
//                                    UserOptionMenu                                                    //
//////////////////////////////////////////////////////////////////////////////////////////////////////////

interface MenuProps {
  row: any;
  onRefresh: () => void;
}

function UserOptionMenu({ row, onRefresh }: MenuProps) {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const [openEditRole, setOpenEditRole] = React.useState(false);
  const [openDeleteUser, setOpenDeleteUser] = React.useState(false);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  return (
    <div className="relative">
      <button
        onClick={handleClick}
        className="size-8 rounded-lg hover:bg-slate-100 transition-colors flex items-center justify-center"
      >
        <span className="material-symbols-outlined text-[20px] text-slate-400">more_vert</span>
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={handleCloseMenu} />
          <div className="absolute right-0 top-full mt-1 w-48 bg-white rounded-xl shadow-lg border border-slate-200 py-1.5 z-50">
            <button
              onClick={() => { setOpenEditRole(true); setAnchorEl(null); }}
              className="w-full flex items-center gap-3 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
            >
              <span className="material-symbols-outlined text-[18px]">manage_accounts</span>
              Edit role
            </button>
            <div className="border-t border-slate-100 my-1" />
            <button
              onClick={() => { setOpenDeleteUser(true); setAnchorEl(null); }}
              className="w-full flex items-center gap-3 px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
            >
              <span className="material-symbols-outlined text-[18px]">person_remove</span>
              Remove user
            </button>
          </div>
        </>
      )}

      <TailwindModal
        isOpen={openEditRole}
        onClose={() => setOpenEditRole(false)}
        title="Edit Role"
        description={`Choose a new role for ${row.firstName} ${row.lastName}.`}
        size="sm"
      >
        <ContentEditRole
          row={row}
          handleCloseEditRole={() => setOpenEditRole(false)}
          onRefresh={onRefresh}
        />
      </TailwindModal>

      <TailwindModal
        isOpen={openDeleteUser}
        onClose={() => setOpenDeleteUser(false)}
        title="Remove User"
        description={`Are you sure you want to remove ${row.firstName} ${row.lastName} from the organization?`}
        size="sm"
      >
        <ContentDeleteUser
          row={row}
          handleCloseDeleteUser={() => setOpenDeleteUser(false)}
          onRefresh={onRefresh}
        />
      </TailwindModal>
    </div>
  );
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////
//                                    Users Table                                                       //
//////////////////////////////////////////////////////////////////////////////////////////////////////////

interface UsersTableProps {
  onRefresh: () => void;
  user: any;
  userRows: any[];
  searchQuery: string;
}

function UsersTable({ onRefresh, user, userRows, searchQuery }: UsersTableProps) {
  const [sortField, setSortField] = useState<string>('fullName');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const filteredRows = userRows.filter(row => {
    if (!row) return false;
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    const fullName = `${row.firstName || ''} ${row.lastName || ''}`.toLowerCase();
    return fullName.includes(query) || row.emailAddress?.toLowerCase().includes(query);
  });

  const sortedRows = [...filteredRows].sort((a, b) => {
    let aVal: any, bVal: any;

    switch (sortField) {
      case 'fullName':
        aVal = `${a.firstName || ''} ${a.lastName || ''}`;
        bVal = `${b.firstName || ''} ${b.lastName || ''}`;
        break;
      case 'emailAddress':
        aVal = a.emailAddress || '';
        bVal = b.emailAddress || '';
        break;
      case 'role':
        aVal = a.role || '';
        bVal = b.role || '';
        break;
      case 'lastConnection':
        aVal = new Date(a.lastConnection || 0).getTime();
        bVal = new Date(b.lastConnection || 0).getTime();
        break;
      default:
        return 0;
    }

    if (typeof aVal === 'string') {
      return sortDirection === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
    }
    return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
  });

  const SortIcon = ({ field }: { field: string }) => (
    <span className={cn(
      "material-symbols-outlined text-[16px] ml-1 transition-colors",
      sortField === field ? "text-slate-700" : "text-slate-300"
    )}>
      {sortField === field && sortDirection === 'desc' ? 'arrow_downward' : 'arrow_upward'}
    </span>
  );

  if (sortedRows.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <div className="size-16 rounded-full bg-slate-50 flex items-center justify-center mb-4">
          <span className="material-symbols-outlined text-[32px] text-slate-300">group_off</span>
        </div>
        <p className="text-sm font-medium text-slate-500">
          {searchQuery ? 'No users match your search' : 'No users found'}
        </p>
        <p className="text-xs text-slate-400 mt-1">
          {searchQuery ? 'Try a different search term' : 'Invite users to get started'}
        </p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="border-b border-slate-100">
            {[
              { field: 'fullName', label: 'Name' },
              { field: 'emailAddress', label: 'Email' },
              { field: 'role', label: 'Role' },
              { field: 'commercialPlanName', label: 'Plan', hiddenOnMobile: true },
              { field: 'lastConnection', label: 'Last Active', hiddenOnMobile: true },
            ].map(({ field, label, hiddenOnMobile }) => (
              <th
                key={field}
                className={cn(
                  "px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider cursor-pointer hover:text-slate-700 transition-colors select-none",
                  hiddenOnMobile && "hidden lg:table-cell"
                )}
                onClick={() => handleSort(field)}
              >
                <div className="flex items-center">
                  {label}
                  <SortIcon field={field} />
                </div>
              </th>
            ))}
            <th className="px-4 py-3 w-16" />
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-50">
          {sortedRows.map((row) => {
            const isCurrentUser = row.id === user?.id;
            return (
              <tr
                key={row.id}
                className={cn(
                  "group transition-colors",
                  isCurrentUser ? "bg-primary/[0.02]" : "hover:bg-slate-50/50"
                )}
              >
                <td className="px-4 py-3.5">
                  <div className="flex items-center gap-3">
                    <div className={cn(
                      "size-9 rounded-full flex items-center justify-center text-sm font-bold shrink-0",
                      isCurrentUser ? "bg-primary/10 text-primary" : "bg-slate-100 text-slate-500"
                    )}>
                      {(row.firstName?.[0] || '').toUpperCase()}{(row.lastName?.[0] || '').toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-slate-900 truncate">
                          {row.firstName} {row.lastName}
                        </span>
                        {isCurrentUser && (
                          <span className="text-[10px] font-bold text-primary bg-primary/10 px-1.5 py-0.5 rounded tracking-wide shrink-0">
                            YOU
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3.5">
                  <span className="text-sm text-slate-600">{row.emailAddress}</span>
                </td>
                <td className="px-4 py-3.5">
                  <span className={cn(
                    "inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold",
                    RoleBadgeColors[row.role as keyof typeof RoleBadgeColors] || 'bg-slate-100 text-slate-600'
                  )}>
                    {RoleEnum[row.role as keyof typeof RoleEnum] || row.role}
                  </span>
                </td>
                <td className="px-4 py-3.5 hidden lg:table-cell">
                  <span className="text-sm text-slate-500">{row.commercialPlanName || '—'}</span>
                </td>
                <td className="px-4 py-3.5 hidden lg:table-cell">
                  <span className="text-sm text-slate-400">
                    {row.lastConnection ? formatDate(getCurrentTzDate(row.lastConnection)) : '—'}
                  </span>
                </td>
                <td className="px-4 py-3.5">
                  <div className="flex justify-end">
                    {isCurrentUser ? (
                      <div className="size-8 flex items-center justify-center">
                        <span className="material-symbols-outlined text-[20px] text-primary">person_pin</span>
                      </div>
                    ) : user?.role_slug === 'admin' ? (
                      <UserOptionMenu row={row} onRefresh={onRefresh} />
                    ) : null}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////
//                                    Organization Info Modal                                           //
//////////////////////////////////////////////////////////////////////////////////////////////////////////

interface ContentOrganizationProps {
  organization: any;
  onRefresh: () => void;
}

function ContentOrganization({ organization, onRefresh }: ContentOrganizationProps) {
  const [isModifiable, setIsModifiable] = React.useState(false);
  const [openOrganization, setOpenOrganization] = React.useState(false);
  const editOrganizationMutation = useEditOrganization();

  const handleCloseOrganization = () => {
    setOpenOrganization(false);
    setIsModifiable(false);
  };

  const OrganizationSchema = Yup.object().shape({
    name: Yup.string().required("Organization name required"),
    address: Yup.string().required("Address required"),
    city: Yup.string().required("City required"),
    post_code: Yup.string().required("Post code required"),
    country: Yup.string().required("Country required"),
    billing_address: Yup.string().required("Billing address required"),
    billing_city: Yup.string().required("Billing city required"),
    billing_post_code: Yup.string().required("Billing post code required"),
    billing_country: Yup.string().required("Country required"),
  });

  const formik = useFormik({
    initialValues: {
      name: "",
      address: "",
      city: "",
      post_code: "",
      country: "",
      billing_address: "",
      billing_city: "",
      billing_post_code: "",
      billing_country: "",
    },
    validationSchema: OrganizationSchema,
    onSubmit: () => {
      setIsModifiable(false);
      editOrganizationMutation.mutate(
        {
          name: values.name,
          address: values.address,
          city: values.city,
          post_code: values.post_code,
          country: values.country,
          billing_address: values.billing_address,
          billing_city: values.billing_city,
          billing_post_code: values.billing_post_code,
          billing_country: values.billing_country,
        },
        {
          onSuccess: () => {
            onRefresh();
          },
        },
      );
    },
  });

  useEffect(() => {
    if (organization && Object.keys(organization).length > 0) {
      formik.setValues(organization);
    }
  }, [organization]);

  const { errors, touched, values, handleSubmit, getFieldProps } = formik;

  const readOnlyStyle = !isModifiable ? "border-0 border-b border-slate-200 rounded-none bg-transparent" : "";

  return (
    <div>
      <TailwindButton
        variant="secondary"
        size="sm"
        icon="corporate_fare"
        onClick={() => setOpenOrganization(true)}
      >
        Organization Info
      </TailwindButton>

      <TailwindModal
        isOpen={openOrganization}
        onClose={handleCloseOrganization}
        title="Organization Information"
        description="View and edit your organization details."
        size="2xl"
        footer={
          isModifiable ? (
            <>
              <TailwindButton
                variant="ghost"
                onClick={() => {
                  setIsModifiable(false);
                  formik.setValues(organization);
                }}
              >
                Cancel
              </TailwindButton>
              <TailwindButton
                variant="primary"
                icon="save"
                onClick={() => handleSubmit()}
              >
                Save Changes
              </TailwindButton>
            </>
          ) : (
            <TailwindButton
              variant="ghost"
              onClick={handleCloseOrganization}
            >
              Close
            </TailwindButton>
          )
        }
      >
        <FormikProvider value={formik}>
          <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider">General</h3>
              {!isModifiable && (
                <TailwindButton
                  variant="ghost"
                  size="sm"
                  icon="edit"
                  onClick={() => setIsModifiable(true)}
                >
                  Edit
                </TailwindButton>
              )}
            </div>

            <div className="space-y-4">
              <TailwindInput
                fullWidth
                autoComplete="username"
                label="Name"
                {...getFieldProps("name")}
                error={touched.name && errors.name ? errors.name : undefined}
                readOnly={!isModifiable}
                className={readOnlyStyle}
              />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-4">
                  <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Address</p>
                  <TailwindInput
                    fullWidth
                    label="Street"
                    {...getFieldProps("address")}
                    error={touched.address && errors.address ? errors.address : undefined}
                    readOnly={!isModifiable}
                    className={readOnlyStyle}
                  />
                  <TailwindInput
                    fullWidth
                    label="City"
                    {...getFieldProps("city")}
                    error={touched.city && errors.city ? errors.city : undefined}
                    readOnly={!isModifiable}
                    className={readOnlyStyle}
                  />
                  <TailwindInput
                    fullWidth
                    label="Post code"
                    {...getFieldProps("post_code")}
                    error={touched.post_code && errors.post_code ? errors.post_code : undefined}
                    readOnly={!isModifiable}
                    className={readOnlyStyle}
                  />
                  <TailwindInput
                    fullWidth
                    label="Country"
                    {...getFieldProps("country")}
                    error={touched.country && errors.country ? errors.country : undefined}
                    readOnly={!isModifiable}
                    className={readOnlyStyle}
                  />
                </div>

                <div className="space-y-4">
                  <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Billing Address</p>
                  <TailwindInput
                    fullWidth
                    label="Street"
                    {...getFieldProps("billing_address")}
                    error={touched.billing_address && errors.billing_address ? errors.billing_address : undefined}
                    readOnly={!isModifiable}
                    className={readOnlyStyle}
                  />
                  <TailwindInput
                    fullWidth
                    label="City"
                    {...getFieldProps("billing_city")}
                    error={touched.billing_city && errors.billing_city ? errors.billing_city : undefined}
                    readOnly={!isModifiable}
                    className={readOnlyStyle}
                  />
                  <TailwindInput
                    fullWidth
                    label="Post code"
                    {...getFieldProps("billing_post_code")}
                    error={touched.billing_post_code && errors.billing_post_code ? errors.billing_post_code : undefined}
                    readOnly={!isModifiable}
                    className={readOnlyStyle}
                  />
                  <TailwindInput
                    fullWidth
                    label="Country"
                    {...getFieldProps("billing_country")}
                    error={touched.billing_country && errors.billing_country ? errors.billing_country : undefined}
                    readOnly={!isModifiable}
                    className={readOnlyStyle}
                  />
                </div>
              </div>
            </div>
          </Form>
        </FormikProvider>
      </TailwindModal>
    </div>
  );
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////
//                                    Edit Role Modal                                                   //
//////////////////////////////////////////////////////////////////////////////////////////////////////////

interface ContentEditRoleProps {
  row: any;
  handleCloseEditRole: () => void;
  onRefresh: () => void;
}

function ContentEditRole({ row, handleCloseEditRole, onRefresh }: ContentEditRoleProps) {
  const [selectedRole, setSelectedRole] = React.useState('');
  const [roleError, setRoleError] = React.useState(false);
  const editRoleMutation = useEditRoleUser();

  const roleOptions = [
    { value: 'admin', label: 'Administrator' },
    { value: 'user', label: 'User' },
    { value: 'billing', label: 'Billing' },
  ];

  const handleEditRoleValidate = () => {
    if (selectedRole && !roleError && selectedRole !== row.role) {
      handleCloseEditRole();
      editRoleMutation.mutate(
        { userId: row.id, roleSlug: selectedRole },
        { onSuccess: () => onRefresh() },
      );
    }
  };

  const handleChangeRole = (value: string) => {
    setSelectedRole(value);
    setRoleError(row.role === value);
  };

  useEffect(() => {
    if (row) {
      setSelectedRole(row.role);
    }
  }, [row]);

  return (
    <div className="space-y-4">
      <TailwindSelect
        value={selectedRole}
        onChange={handleChangeRole}
        options={roleOptions}
        label="Role"
        error={roleError ? `This user already has the ${RoleEnum[selectedRole as keyof typeof RoleEnum]} role` : undefined}
      />
      <div className="flex justify-end gap-3">
        <TailwindButton
          variant="ghost"
          onClick={handleCloseEditRole}
        >
          Cancel
        </TailwindButton>
        <TailwindButton
          variant="primary"
          icon="manage_accounts"
          onClick={handleEditRoleValidate}
          disabled={roleError || !selectedRole}
        >
          Update Role
        </TailwindButton>
      </div>
    </div>
  );
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////
//                                    Add User Modal                                                    //
//////////////////////////////////////////////////////////////////////////////////////////////////////////

function ContentAddUser() {
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState({ message: '', variant: '' });
  const [error, setError] = useState(false);
  const [openAddUser, setOpenAddUser] = useState(false);
  const addUserMutation = useAddUser();

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(event.target.value);
    setError(false);
    setEmailError({ message: '', variant: '' });
  };

  const handleAgree = () => {
    const isValidEmail = /\S+@\S+\.\S+/.test(email);

    if (isValidEmail && email) {
      setError(false);
      addUserMutation.mutate(
        { email },
        {
          onSuccess: () => {
            setEmailError({
              message: 'An invitation email has been sent to this address',
              variant: 'success',
            });
            setEmail('');
          },
          onError: (err) => {
            const detail = err instanceof ApiError && err.body
              ? (err.body as { detail?: string }).detail
              : undefined;
            setEmailError({
              message: detail || 'An error occurred while sending the invitation',
              variant: 'error',
            });
          },
        },
      );
    } else {
      setError(true);
    }
  };

  const handleClose = () => {
    setOpenAddUser(false);
    setEmail('');
    setEmailError({ message: '', variant: '' });
    setError(false);
  };

  return (
    <div>
      <TailwindButton
        variant="primary"
        size="sm"
        icon="person_add"
        onClick={() => setOpenAddUser(true)}
      >
        Invite User
      </TailwindButton>

      <TailwindModal
        isOpen={openAddUser}
        onClose={handleClose}
        title="Invite User"
        description="Enter the email address of the person you want to invite to your organization."
        size="sm"
        footer={
          <>
            <TailwindButton
              variant="ghost"
              onClick={handleClose}
            >
              Cancel
            </TailwindButton>
            <TailwindButton
              variant="primary"
              icon="send"
              onClick={handleAgree}
            >
              Send Invitation
            </TailwindButton>
          </>
        }
      >
        <div className="space-y-3">
          <TailwindInput
            label="Email Address"
            placeholder="colleague@company.com"
            fullWidth
            value={email}
            onChange={handleChange}
            onKeyDown={(e) => { if (e.key === 'Enter') handleAgree(); }}
            error={error ? "Enter a valid email address" : undefined}
            autoFocus
          />
          {emailError.message && (
            <div className={cn(
              "flex items-center gap-2 px-4 py-3 rounded-lg text-sm",
              emailError.variant === 'success'
                ? "bg-green-50 text-green-700 border border-green-200"
                : "bg-red-50 text-red-700 border border-red-200"
            )}>
              <span className="material-symbols-outlined text-[18px]">
                {emailError.variant === 'success' ? 'check_circle' : 'error'}
              </span>
              {emailError.message}
            </div>
          )}
        </div>
      </TailwindModal>
    </div>
  );
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////
//                                    Delete User Modal                                                 //
//////////////////////////////////////////////////////////////////////////////////////////////////////////

interface ContentDeleteUserProps {
  row: any;
  handleCloseDeleteUser: () => void;
  onRefresh: () => void;
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////
//                                    ERP Connections                                                   //
//////////////////////////////////////////////////////////////////////////////////////////////////////////

function ContentErpConnections({ organizationId, isAdmin }: { organizationId: number; isAdmin: boolean }) {
  const [openErpConnections, setOpenErpConnections] = React.useState(false);

  return (
    <div>
      <TailwindButton
        variant="secondary"
        size="sm"
        icon="cable"
        onClick={() => setOpenErpConnections(true)}
      >
        ERP Connections
      </TailwindButton>
      <ErpConnectionsModal open={openErpConnections} setOpen={setOpenErpConnections} organizationId={organizationId} isAdmin={isAdmin} />
    </div>
  );
}

function ContentDeleteUser({ row, handleCloseDeleteUser, onRefresh }: ContentDeleteUserProps) {
  const deleteUserMutation = useDeleteUser();

  const handleConfirmDelete = () => {
    handleCloseDeleteUser();
    deleteUserMutation.mutate(
      { userId: row.id },
      { onSuccess: () => onRefresh() },
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 p-4 bg-red-50 rounded-lg border border-red-100">
        <span className="material-symbols-outlined text-[24px] text-red-500">warning</span>
        <p className="text-sm text-red-700">
          This action cannot be undone. The user will lose access to all organization resources.
        </p>
      </div>
      <div className="flex justify-end gap-3">
        <TailwindButton
          variant="ghost"
          onClick={handleCloseDeleteUser}
        >
          Cancel
        </TailwindButton>
        <TailwindButton
          variant="danger"
          icon="delete_forever"
          onClick={handleConfirmDelete}
        >
          Remove User
        </TailwindButton>
      </div>
    </div>
  );
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////
//                                    Organization Page                                                 //
//////////////////////////////////////////////////////////////////////////////////////////////////////////

export interface OrganizationPageProps {
  go_to_page: Function;
}

export default function Organization({ entity, user }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [commercialPanelMounted, setCommercialPanelMounted] = useState(false);

  const { data: organization = {
    id: 0, name: '', address: '', city: '', post_code: '', country: '',
    billing_address: '', billing_city: '', billing_post_code: '', billing_country: '', licenses: 0,
  }, refetch: refetchOrg } = useOrganization();

  const { data: rawUsers = [], refetch: refetchUsers } = useOrganizationUsers();

  const users = useMemo(() =>
    rawUsers.map((item: any) => ({
      id: item.id,
      firstName: item.first_name,
      lastName: item.last_name,
      emailAddress: item.email,
      role: item.role_slug,
      lastConnection: item.last_connection,
      commercialPlanName: item.commercial_plan_name,
      commercialPlanId: item.commercial_plan_id,
    })),
    [rawUsers],
  );

  const userAdmin = user?.role_slug === 'admin';

  const onRefresh = () => {
    refetchOrg();
    refetchUsers();
    setCommercialPanelMounted((prev) => !prev);
  };

  // Stats
  const totalUsers = users.length;
  const adminCount = users.filter((u: any) => u.role === 'admin').length;
  const activeRecently = users.filter((u: any) => {
    if (!u.lastConnection) return false;
    const lastActive = new Date(u.lastConnection);
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    return lastActive > thirtyDaysAgo;
  }).length;

  return motionify(
    <div className="flex-grow bg-white min-h-full">
      {/* Page Header */}
      <div className="px-8 py-6 border-b border-slate-100 bg-white sticky top-0 z-10">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
          <div className="flex-1">
            <h2 className="text-3xl font-black text-slate-900 tracking-tight">
              {organization.name || 'Organization'}
            </h2>
            <p className="text-sm text-slate-500 mt-2">
              Manage your team, roles, and organization settings
            </p>
          </div>
        </div>
      </div>

      {/* Page Content */}
      <div className="px-8 py-6 flex flex-col gap-6">

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="border border-slate-100 rounded-xl p-5 bg-white">
            <div className="flex items-center justify-between mb-3">
              <div className="size-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <span className="material-symbols-outlined text-[20px] text-primary">group</span>
              </div>
            </div>
            <p className="text-2xl font-bold text-slate-900">{totalUsers}</p>
            <p className="text-xs text-slate-500 mt-1">Total Members</p>
          </div>
          <div className="border border-slate-100 rounded-xl p-5 bg-white">
            <div className="flex items-center justify-between mb-3">
              <div className="size-10 rounded-lg bg-amber-50 flex items-center justify-center">
                <span className="material-symbols-outlined text-[20px] text-amber-600">shield_person</span>
              </div>
            </div>
            <p className="text-2xl font-bold text-slate-900">{adminCount}</p>
            <p className="text-xs text-slate-500 mt-1">Administrators</p>
          </div>
          <div className="border border-slate-100 rounded-xl p-5 bg-white">
            <div className="flex items-center justify-between mb-3">
              <div className="size-10 rounded-lg bg-green-50 flex items-center justify-center">
                <span className="material-symbols-outlined text-[20px] text-green-600">trending_up</span>
              </div>
            </div>
            <p className="text-2xl font-bold text-slate-900">{activeRecently}</p>
            <p className="text-xs text-slate-500 mt-1">Active (last 30 days)</p>
          </div>
        </div>

        {/* Users Section */}
        <div className="border border-slate-100 rounded-xl overflow-hidden shadow-sm">
          {/* Toolbar */}
          <div className="px-6 py-4 flex items-center gap-3 border-b border-slate-100 bg-slate-50/30">
            {/* Search */}
            <div className="relative flex-1 max-w-sm">
              <span className="material-symbols-outlined text-[18px] text-slate-400 absolute left-3 top-1/2 -translate-y-1/2">
                search
              </span>
              <input
                type="text"
                placeholder="Search members..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all placeholder:text-slate-400"
              />
            </div>

            <div className="flex-grow" />

            {/* Admin Actions */}
            {userAdmin ? (
              <div className="flex items-center gap-2">
                <ContentOrganization organization={organization} onRefresh={onRefresh} />
                <CommercialPlanPanel organization={organization} users={users} mounted={commercialPanelMounted} />
                <ContentErpConnections organizationId={organization.id} isAdmin={true} />
                <ContentAddUser />
              </div>
            ) : (
              <ContentErpConnections organizationId={organization.id} isAdmin={false} />
            )}
          </div>

          {/* Table */}
          <div className="bg-white">
            <UsersTable
              onRefresh={onRefresh}
              user={user}
              userRows={users}
              searchQuery={searchQuery}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
